import { getGuestPersona } from "@src/common/guest-personas";
import { getPresenceColorForUser } from "@src/common/presence-colors";
import {
	TOUCHPAD_ZOOM_SPEED_STORAGE_KEY,
	clampZoomSpeed,
} from "@src/components/board/mouse-touchpad-panel/mouse-touchpad-store";
import {
	COLOR_SCHEME_STORAGE_KEY,
	toThemePreference,
} from "@src/components/layouts/providers/color-scheme-store";
import { getGuestSessionId } from "@src/utils/guest-session";
import {
	startTransition,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { type UserStore, userStoreSchema } from "./schema";
import { UserProfileContext, type UserState } from "./user-context";

function getInitialColor(user: UserStore | null): string {
	if (user?.presenceColor) return user.presenceColor;
	if (user?.id) return getPresenceColorForUser(user.id);
	return getGuestPersona(getGuestSessionId()).color;
}

export const UserProvider = ({
	user,
	children,
}: { user: UserStore | null; children: React.ReactNode }) => {
	const [userState, setUserState] = useState<UserStore | null>(user);
	const [cardColorState, setCardColorState] = useState(() =>
		getInitialColor(user),
	);

	useEffect(() => {
		setUserState(user);
		setCardColorState(getInitialColor(user));
	}, [user]);

	useEffect(() => {
		if (user?.appearance) {
			const preference = toThemePreference(user.appearance);
			window.localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, preference);
			window.dispatchEvent(
				new CustomEvent("mantine-local-storage", {
					detail: { key: COLOR_SCHEME_STORAGE_KEY, value: preference },
				}),
			);
		}
		if (typeof user?.zoomSpeed === "number" && !Number.isNaN(user.zoomSpeed)) {
			const clampedSpeed = clampZoomSpeed(user.zoomSpeed);
			window.localStorage.setItem(
				TOUCHPAD_ZOOM_SPEED_STORAGE_KEY,
				JSON.stringify(clampedSpeed),
			);
			window.dispatchEvent(
				new CustomEvent("mantine-local-storage", {
					detail: {
						key: TOUCHPAD_ZOOM_SPEED_STORAGE_KEY,
						value: clampedSpeed,
					},
				}),
			);
		}
	}, [user?.appearance, user?.zoomSpeed]);

	const setUser = useCallback((user: Partial<UserStore>) => {
		startTransition(() => {
			setUserState((prev) => {
				if (!prev) return prev;
				const parsed = userStoreSchema.safeParse({ ...prev, ...user });
				if (parsed.success) {
					return parsed.data;
				}
				if (import.meta.env.DEV) {
					console.warn("setUser: invalid partial update", parsed.error);
				}
				return prev;
			});
		});
	}, []);

	const setCardColor = useCallback((color: string) => {
		setCardColorState(color);
	}, []);

	const contextValue = useMemo(
		() => ({
			user: [userState, setUser] as UserState,
			color: [cardColorState, setCardColor] as [
				string,
				(color: string) => void,
			],
		}),
		[userState, setUser, cardColorState, setCardColor],
	);

	return (
		<UserProfileContext.Provider value={contextValue}>
			{children}
		</UserProfileContext.Provider>
	);
};
